import React, { useState, memo, Component } from 'react';
import { BookmarkIcon, FlameIcon, ZapIcon, HeartIcon, BookOpenIcon, HeartHandshakeIcon, UsersIcon, ListIcon, AlertCircleIcon, MapPinIcon, UserIcon, LinkIcon, GlobeIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FilterSidebar, FilterSidebarWrapper, NavItem } from '../components/UI/FilterSidebar';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { useDarkMode } from '../context/DarkModeContext';
const endingTypeData = [{
  id: 'hea',
  name: 'HEA',
  fullName: 'Happily Ever After',
  count: 0,
  description: 'A fully resolved, joyful ending where all major conflicts are settled and the couple (or protagonist) ends up in a lasting, happy situation. The gold standard of romance.',
  examples: ['Engagement', 'Marriage', 'Reunion', 'Found Family'],
  color: {
    light: 'bg-pink-50 text-pink-700 hover:bg-pink-100',
    dark: 'bg-pink-900/30 text-pink-300 hover:bg-pink-900/50'
  },
  emoji: '💕'
}, {
  id: 'hfn',
  name: 'HFN',
  fullName: 'Happy For Now',
  count: 0,
  description: 'A hopeful, satisfying ending without full resolution. The couple is together and happy, but the future remains open — often used in series where the story continues.',
  examples: ['Together but open', 'Series continues', 'Hopeful close'],
  color: {
    light: 'bg-rose-50 text-rose-700 hover:bg-rose-100',
    dark: 'bg-rose-900/30 text-rose-300 hover:bg-rose-900/50'
  },
  emoji: '🌸'
}, {
  id: 'cliffhanger',
  name: 'Cliffhanger',
  fullName: 'Cliffhanger',
  count: 0,
  description: 'The story ends on unresolved tension or a shocking revelation, leading directly into the next book. Expect to immediately reach for the sequel.',
  examples: ['Unresolved conflict', 'Shocking reveal', 'Series setup'],
  color: {
    light: 'bg-amber-50 text-amber-700 hover:bg-amber-100',
    dark: 'bg-amber-900/30 text-amber-300 hover:bg-amber-900/50'
  },
  emoji: '⚡'
}, {
  id: 'tragic',
  name: 'Tragic',
  fullName: 'Tragic Ending',
  count: 0,
  description: 'The story ends in loss, death, or irreversible heartbreak. Not for the faint of heart — but often the most emotionally resonant and memorable endings.',
  examples: ['Death', 'Separation', 'Sacrifice', 'Irreversible loss'],
  color: {
    light: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    dark: 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
  },
  emoji: '🖤'
}, {
  id: 'bittersweet',
  name: 'Bittersweet',
  fullName: 'Bittersweet Ending',
  count: 0,
  description: 'A mix of joy and sorrow — something meaningful is gained, but something is also lost. These endings linger long after the final page.',
  examples: ['Partial resolution', 'Growth through loss', 'Acceptance'],
  color: {
    light: 'bg-purple-50 text-purple-700 hover:bg-purple-100',
    dark: 'bg-purple-900/30 text-purple-300 hover:bg-purple-900/50'
  },
  emoji: '🌧️'
}];
const contentNavItems: NavItem[] = [{
  label: 'View All Tags',
  href: '/tagged-books?type=all',
  icon: ListIcon
}, {
  label: 'Tropes',
  href: '/tropes',
  icon: HeartHandshakeIcon,
  subItems: [{
    label: 'Romance',
    href: '/tropes?category=romance'
  }, {
    label: 'Fantasy',
    href: '/tropes?category=fantasy'
  }, {
    label: 'Mystery',
    href: '/tropes?category=mystery'
  }, {
    label: 'Sci-Fi',
    href: '/tropes?category=scifi'
  }, {
    label: 'Horror',
    href: '/tropes?category=horror'
  }, {
    label: 'General',
    href: '/tropes?category=general'
  }]
}, {
  label: 'Characters',
  href: '/characters',
  icon: UserIcon,
  subItems: [{
    label: 'Romance',
    href: '/characters?category=romance'
  }, {
    label: 'Fantasy',
    href: '/characters?category=fantasy'
  }, {
    label: 'Mystery',
    href: '/characters?category=mystery'
  }, {
    label: 'Sci-Fi',
    href: '/characters?category=scifi'
  }, {
    label: 'Horror',
    href: '/characters?category=horror'
  }, {
    label: 'General',
    href: '/characters?category=general'
  }]
}, {
  label: 'Relationship Types',
  href: '/relationship-types',
  icon: UsersIcon,
  subItems: [{
    label: 'Romance',
    href: '/relationship-types?category=romance'
  }, {
    label: 'Fantasy',
    href: '/relationship-types?category=fantasy'
  }, {
    label: 'Mystery',
    href: '/relationship-types?category=mystery'
  }, {
    label: 'General',
    href: '/relationship-types?category=general'
  }]
}, {
  label: 'Pairing Types',
  href: '/pairing-types',
  icon: LinkIcon,
  subItems: [{
    label: 'Monogamous',
    href: '/pairing-types?category=monogamous'
  }, {
    label: 'Why Choose',
    href: '/pairing-types?category=why-choose'
  }, {
    label: 'Closed Triad',
    href: '/pairing-types?category=closed-triad'
  }, {
    label: 'Polyamorous',
    href: '/pairing-types?category=polyamorous'
  }, {
    label: 'NB / Trans Inclusive',
    href: '/pairing-types?category=nb-trans'
  }, {
    label: 'No Romance',
    href: '/pairing-types?category=no-romance'
  }]
}, {
  label: 'Spice & Darkness',
  href: '/spice-darkness',
  icon: FlameIcon,
  subItems: [{
    label: 'Spice Levels',
    href: '/spice-darkness#spice'
  }, {
    label: 'Darkness Levels',
    href: '/spice-darkness#darkness'
  }]
}, {
  label: 'Burn Levels',
  href: '/burn-levels',
  icon: ZapIcon
}, {
  label: 'Ending Types',
  href: '/ending-types',
  icon: BookmarkIcon,
  isActive: true
}, {
  label: 'Representation',
  href: '/representation',
  icon: UsersIcon,
  subItems: [{
    label: 'Sexual Orientation & Gender',
    href: '/representation?category=Sexual+Orientation+%26+Gender'
  }, {
    label: 'Race & Ethnicity',
    href: '/representation?category=Race+%26+Ethnicity'
  }, {
    label: 'Neurodiversity & Disability',
    href: '/representation?category=Neurodiversity+%26+Disability'
  }, {
    label: 'Mental Health',
    href: '/representation?category=Mental+Health'
  }]
}, {
  label: 'Cultural Identity',
  href: '/cultural-identity',
  icon: GlobeIcon
}, {
  label: 'Themes',
  href: '/themes',
  icon: BookOpenIcon,
  subItems: [{
    label: 'Personal Growth',
    href: '/themes?category=Personal+Growth'
  }, {
    label: 'Destiny',
    href: '/themes?category=Destiny'
  }, {
    label: 'Power',
    href: '/themes?category=Power'
  }, {
    label: 'Morality',
    href: '/themes?category=Morality'
  }, {
    label: 'Relationships',
    href: '/themes?category=Relationships'
  }, {
    label: 'Identity',
    href: '/themes?category=Identity'
  }]
}, {
  label: 'Settings',
  href: '/settings',
  icon: MapPinIcon,
  subItems: [{
    label: 'Contemporary',
    href: '/settings?category=Contemporary'
  }, {
    label: 'Historical',
    href: '/settings?category=Historical'
  }, {
    label: 'Fantasy',
    href: '/settings?category=Fantasy'
  }, {
    label: 'Science Fiction',
    href: '/settings?category=Science%20Fiction'
  }]
}, {
  label: 'Content Warnings',
  href: '/content-warnings',
  icon: AlertCircleIcon
}];
const EndingTypes: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    darkMode
  } = useDarkMode();
  const filteredEndings = endingTypeData.filter(ending => ending.name.toLowerCase().includes(searchQuery.toLowerCase()) || ending.fullName.toLowerCase().includes(searchQuery.toLowerCase()) || ending.description.toLowerCase().includes(searchQuery.toLowerCase()));
  return <div className={dk.page} data-id="element-9515" data-mp="1lriijomgnk3b">
      <ContentPageHeader title="Ending Types" icon={getContentPageIcon('endingtypes')} searchPlaceholder="Search ending types..." searchQuery={searchQuery} setSearchQuery={setSearchQuery} showFilters={showFilters} setShowFilters={setShowFilters} data-id="element-9516" data-mp="2j98vfmsff8uf" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-9517" data-mp="2qrghac2qcskv">
        <div className="flex gap-8" data-id="element-9518" data-mp="2vrcm2dj94o1h">
          <FilterSidebarWrapper isOpen={showFilters} onClose={() => setShowFilters(false)} isCollapsed={false} onToggleCollapse={() => {}} data-id="element-9519" data-mp="2vq9styygi9ie">
            <FilterSidebar title="Browse" navigation={contentNavItems} sections={[]} onReset={() => {}} searchable={false} data-id="element-9520" data-mp="34qzp13rmq1wn" />
          </FilterSidebarWrapper>

          <main className="flex-1 min-w-0" data-id="element-9521" data-mp="3ejx7xb0j9svs">
            <div className="text-center mb-8" data-id="element-9522" data-mp="3jh6v0k0g9sgg">
              <h1 className={`text-2xl sm:text-3xl font-bold flex justify-center items-center gap-2 ${dk.textHeading}`} data-id="element-9523" data-mp="3jj0405yjcorm">
                <BookmarkIcon className="h-6 w-6 text-indigo-500" data-id="element-9524" data-mp="3sgu0pi07pkfb" />
                Ending Types
              </h1>
              <p className={`text-base sm:text-lg max-w-2xl mx-auto mt-3 ${dk.textSecondary}`} data-id="element-9525" data-mp="2viixm10wrf9e">
                Know what you're getting into before the final page. Browse
                books by how their story concludes.
              </p>
            </div>

            <div className="space-y-4 sm:space-y-6" data-id="element-9526" data-mp="3a75n2vk91nec">
              {filteredEndings.map(ending => <div key={ending.id} className={`rounded-lg shadow-md p-4 sm:p-6 ${dk.card}`} data-id="element-9527" data-mp="3e6nzt41x2s63">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-3 sm:mb-4" data-id="element-9528" data-mp="3j8lkm2pytv15">
                    <h2 className={`text-lg sm:text-xl font-semibold flex items-center gap-3 ${dk.textHeading}`} data-id="element-9529" data-mp="3t5hxslqt75c1">
                      <span className="text-2xl" role="img" aria-hidden="true" data-id="element-9530" data-mp="1ttxdafw8f39z">
                        {ending.emoji}
                      </span>
                      <span data-id="element-9531" data-mp="fw2padvw83bq">
                        {ending.name}
                        {ending.name !== ending.fullName && <span className={`ml-2 text-sm font-normal ${dk.textSecondary}`} data-id="element-9532" data-mp="b3jgdu85hwd9">
                            — {ending.fullName}
                          </span>}
                      </span>
                    </h2>
                    <Link to={`/tagged-books?tag=${encodeURIComponent(ending.name)}&category=ending`} className={`inline-flex items-center px-3 py-1 ${rt.badgeRadius} text-sm font-medium ${dk.badgeNeutral} transition-colors`} data-id="element-9533" data-mp="ytvu5lq2unbr">
                      {ending.count} books
                    </Link>
                  </div>
                  <p className={`text-sm sm:text-base mb-3 sm:mb-4 ${dk.textSecondary}`} data-id="element-9534" data-mp="3al0yhkvdj5rb">
                    {ending.description}
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-9535" data-mp="373lkbkeaniv1">
                    {ending.examples.map((example, index) => <Link key={index} to={`/tagged-books?tag=${encodeURIComponent(example)}&category=ending&type=${encodeURIComponent(ending.name)}`} className={`inline-flex items-center px-2 sm:px-3 py-1 ${rt.badgeRadius} text-xs sm:text-sm ${darkMode ? ending.color.dark : ending.color.light} transition-colors`} data-id="element-9536" data-mp="3c0lnnnjre7ig" data-mpi={index}>
                        {example}
                      </Link>)}
                  </div>
                </div>)}
            </div>
          </main>
        </div>
      </div>
    </div>;
};
export default EndingTypes;